import {
  Button,
  Checkbox,
  DatePicker,
  Radio,
  Spin,
  Tooltip,
  Upload,
} from "antd";
import { Col, Form, Input, Row } from "antd";
import React, { FC, useEffect, useState } from "react";
import { useUserFunctionalities } from "../../../../utils/UserFunctionalities";
import { IFetchAllDetails } from "../../../../interfaces/IPatient";
import { Document } from "../../PreAuth/PreAuthIhx/PreAuthIhx";
import { CheckOutlined, EyeOutlined, UploadOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import ProceduresCoding from "../../PreAuthClaim/ProcedureList";
import CghsProcedureClaim from "./CghsProcedureClaim";
import { ROUTES } from "../../../../utils/constants";
import { useNavigate } from "react-router-dom";
import ReferralChecklistModal from "../../../modals/ReferralChecklistModal";
import {
  ClaimExtraDocument,
  mapClaimExtraDocumentsToDisplayUrls,
} from "../../../modals/BillRequiredDocumentModal";
import SupportingDocumentChecklistModal from "../../../modals/SupportingDocumentChecklistModal";

type ClaimCghsNewProps = {
  patients: IFetchAllDetails | null;
  documentTypes: Document[];
  documentUrls: {
    documentType: string;
    documentName: string;
    documentUrl: string;
    id: number;
    referenceType: string | null;
    documentLabelType: any;
    deficiencyCheckStatus: any;
    remarks: string | null;
  }[];
  handleFileChange: any;
  handleOpen: any;
  form: any;
  handleSubmit: any;
  handleSubmitOnPortal: any;
  handleApprove: any;
  isApproved: boolean;
  isApprovedInitiated: boolean;
  caseClaimId: any;
  isSubmitting: boolean;
  isSaving: boolean;
  currentActiveStage: any;
  documentRequestResponseDocuments: any;
  handleSaveData: any;
  activeKey: any;
  preAuth: any;
  portalSubmission: boolean;
  loading: boolean;
  handleSaveConfirmationModal: any;
  isIntimating: boolean;
  claimData: any;
  cghsProcedures: any;
  uploadFileList: any;
  billRequiredDocuments?: any[];
  claimExtraDocuments?: ClaimExtraDocument[];
  onSupportingFileSelect?: (
    doc: any,
    checkedItems: any[],
    file: File,
    allUploads?: Array<{ doc: any; checkedItems: any[] }>,
  ) => void;
  onViewSupportingDocument?: (record: any, documentUrl?: string) => void;
  onDownloadSupportingDocument?: (fileName: string, documentUrl: string) => void;
  onDeleteSupportingDocument?: (record: any, documentUrl?: string) => void;
};

const ClaimCghsNew: FC<ClaimCghsNewProps> = ({
  form,
  patients,
  documentTypes,
  documentUrls,
  handleFileChange,
  handleOpen,
  handleSubmit,
  handleSubmitOnPortal,
  handleApprove,
  isApproved,
  isApprovedInitiated,
  caseClaimId,
  isSubmitting,
  isSaving,
  currentActiveStage,
  documentRequestResponseDocuments,
  handleSaveData,
  activeKey,
  preAuth,
  portalSubmission,
  loading,
  handleSaveConfirmationModal,
  isIntimating,
  claimData,
  cghsProcedures,
  uploadFileList,
  billRequiredDocuments = [],
  claimExtraDocuments = [],
  onSupportingFileSelect,
  onViewSupportingDocument,
  onDownloadSupportingDocument,
  onDeleteSupportingDocument,
}) => {
  const { hasFunctionality } = useUserFunctionalities();
  const [referralChecklistModal, setReferralChecklistModal] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const showData =
    preAuth?.stepperData?.currentVisitStage?.visitStageId <=
    parseInt(activeKey);
  const { auth } = useSelector((state: RootState) => state);
  const isAdditionalAmountPaid = Form.useWatch("additionalAmountPaid", form);
  const isSupportingDocument = Form.useWatch("supportingDocument", form);

  const [updatedRequestDocumentUrls, setUpdatedRequestDocumentUrls] =
    useState<any>();
  const [updatedResponseDocumentUrls, setUpdatedResponseDocumentUrls] =
    useState<any>();

  useEffect(() => {
    const uploadedDocumentUrls: any =
      documentUrls?.filter((document: any) =>
        documentRequestResponseDocuments?.["requestedDocuments"]?.some(
          (doc: any) =>
            doc?.label?.toUpperCase() ===
            document?.documentLabelType?.toUpperCase(),
        ),
      ) || [];

    const billRequiredDisplayUrls =
      mapClaimExtraDocumentsToDisplayUrls(claimExtraDocuments);
    const existingUrls = new Set(
      uploadedDocumentUrls.map((doc: any) => doc.documentUrl),
    );
    const mergedUploadedUrls = [
      ...uploadedDocumentUrls,
      ...billRequiredDisplayUrls.filter(
        (doc) => !existingUrls.has(doc.documentUrl),
      ),
    ];

    setUpdatedRequestDocumentUrls(mergedUploadedUrls);
  }, [documentRequestResponseDocuments, documentUrls, claimExtraDocuments]);

  useEffect(() => {
    const uploadedDocumentUrls: any = documentUrls?.filter((document: any) =>
      documentRequestResponseDocuments?.["responseDocuments"]?.some(
        (doc: any) => doc?.label?.toUpperCase() === document?.documentLabelType?.toUpperCase()
      )
    );

    setUpdatedResponseDocumentUrls(uploadedDocumentUrls);
  }, [documentRequestResponseDocuments, documentUrls]);

  const groupByLabelType = (docs: any[]) => {
    return docs?.reduce((acc: any, doc: any) => {
      const label = doc.documentLabelType;
      if (!acc[label]) acc[label] = [];
      acc[label].push(doc);
      return acc;
    }, {});
  };

  return (
    <div style={{ overflow: "auto", width: "100%" }}>
      {/* <CghsProcedureClaim cghsProcedures={cghsProcedures} /> */}
      <div
        style={{
          textDecoration: "underline",
          fontSize: "16px",
          margin: "16px 0",
          fontWeight: "bold",
        }}
      >
        Claim Details
      </div>
      <Form form={form} layout="vertical">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={7} lg={7} xl={7}>
            <Form.Item
              label="Any amount collecting from beneficiary"
              name="additionalAmountPaid"
              style={{ color: "black" }}
              rules={[
                {
                  required: true,
                  message: "Please select Additional Amount Paid",
                },
              ]}
            >
              <Radio.Group
                options={[
                  {
                    label: "Yes",
                    value: "true",
                  },
                  {
                    label: "No",
                    value: "false",
                  },
                ]}
                disabled={portalSubmission}
              />
            </Form.Item>
          </Col>

          {isAdditionalAmountPaid === "true" ? (
            <Col xs={24} sm={12} md={7} lg={7} xl={7}>
              <Form.Item
                label="Additional Amount Paid by Beneficiary"
                name="amount"
                style={{ color: "#575757" }}
                rules={[
                  {
                    required: true,
                    message: "Please select amount",
                  },
                  {
                    pattern: /^\d+$/,
                    message: "Only Numeric values are allowed!",
                  },
                ]}
              >
                <Input
                  placeholder="Please input Additional amount paid"
                  style={{ color: "black" }}
                  disabled={portalSubmission}
                />
              </Form.Item>
            </Col>
          ) : (
            <></>
          )}

          {/* <Col xs={24} sm={12} md={4} lg={4} xl={4}>
            <Form.Item
              label="Supporting Document"
              name="supportingDocument"
              style={{ color: "black" }}
              rules={[
                {
                  required: true,
                  message: "Please select Supporting Document",
                },
              ]}
            >
              <Radio.Group
                options={[
                  {
                    label: "Yes",
                    value: true,
                  },
                  {
                    label: "No",
                    value: false,
                  },
                ]}
                disabled={portalSubmission}
              />
            </Form.Item>
          </Col> */}

          <Col xs={24} sm={12} md={6} lg={6} xl={6}>
            <Form.Item
              label="Total Bill Amount"
              name="totalBillAmount"
              style={{ color: "#575757" }}
              rules={[
                {
                  required: true,
                  message: "Please select amount",
                },
                {
                  pattern: /^(\d+(\.\d{1,2})?|\.\d{1,2})$/,
                  message:
                    "Only numeric values with up to 2 decimal places are allowed!",
                },
              ]}
            >
              <Input
                placeholder="Please input amount"
                style={{ color: "black" }}
                disabled={portalSubmission}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={6} lg={6} xl={6}>
            <Form.Item
              label="Total Claim Amount"
              name="totalClaimAmount"
              style={{ color: "#575757" }}
              rules={[
                {
                  required: true,
                  message: "Please select amount",
                },
                {
                  pattern: /^(\d+(\.\d{1,2})?|\.\d{1,2})$/,
                  message:
                    "Only numeric values with up to 2 decimal places are allowed!",
                },
                // {
                //   pattern: /^(\d+(\.\d+)?|\.\d+)$/,
                //   message: "Only Numeric values are allowed!",
                // },
              ]}
            >
              <Input
                placeholder="Please input amount"
                style={{ color: "black" }}
                disabled={portalSubmission}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={4} lg={4} xl={4}>
            <Form.Item
              label="Hospital Bill Date"
              name="hospitalBillDate"
              style={{ color: "#575757" }}
              rules={[
                {
                  required: true,
                  message: "Please input Date",
                },
              ]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format={"DD-MM-YYYY HH:mm:ss"}
                disabled={portalSubmission}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={6} lg={6} xl={6}>
            <Form.Item
              label="Hospital Bill Number"
              name="hospitalBillNumber"
              style={{ color: "#575757" }}
              rules={[
                {
                  required: true,
                  message: "Please input Hospital Bill Number",
                },
              ]}
            >
              <Input
                placeholder="Please input Hospital Bill Number"
                style={{ color: "black" }}
                disabled={portalSubmission}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={6} lg={6} xl={6}>
            <Form.Item
              label="Hospital Bill Amount"
              name="hospitalBillAmount"
              style={{ color: "#575757" }}
              rules={[
                {
                  pattern: /^\d+$/,
                  message: "Only Numeric values are allowed!",
                },
                {
                  required: true,
                  message: "Please input Hospital Bill Amount",
                },
              ]}
            >
              <Input
                placeholder="Please input Hospital Bill Amount"
                style={{ color: "black" }}
                disabled={portalSubmission}
              />
            </Form.Item>
          </Col>

          {false ? ( //patients?.visitInfoResponse?.visitType?.toUpperCase() === "OP"
            <></>
          ) : (
            <Col xs={24} sm={12} md={6} lg={6} xl={6}>
              <Form.Item
                label="Drug Amount"
                name="drugAmount"
                style={{ color: "#575757" }}
                rules={[
                  {
                    pattern: /^\d+$/,
                    message: "Only Numeric values are allowed!",
                  },
                ]}
              >
                <Input
                  placeholder="Please input Drug amount"
                  style={{ color: "black" }}
                  disabled={portalSubmission}
                />
              </Form.Item>
            </Col>
          )}

          {false ? ( //patients?.visitInfoResponse?.visitType?.toUpperCase() === "OP"
            <></>
          ) : (
            <Col xs={24} sm={12} md={6} lg={6} xl={6}>
              <Form.Item
                label="Consumable amount"
                name="consumableAmount"
                style={{ color: "#575757" }}
                rules={[
                  {
                    pattern: /^\d+$/,
                    message: "Only Numeric values are allowed!",
                  },
                ]}
              >
                <Input
                  placeholder="Please input consumable Amount"
                  style={{ color: "black" }}
                  disabled={portalSubmission}
                />
              </Form.Item>
            </Col>
          )}

          {patients?.visitInfoResponse?.visitType?.toUpperCase() === "OP" ? ( //
            <></>
          ) : (
            <Col xs={24} sm={12} md={6} lg={6} xl={6}>
              <Form.Item
                label="Post Discharge Medicine Amount"
                name="postDischargeMedicineAmount"
                style={{ color: "#575757" }}
                rules={[
                  {
                    pattern: /^\d+$/,
                    message: "Only Numeric values are allowed!",
                  },
                ]}
              >
                <Input
                  placeholder="Please input Post Discharge Medicine Amount"
                  style={{ color: "black" }}
                  disabled={portalSubmission}
                />
              </Form.Item>
            </Col>
          )}

          {patients?.visitInfoResponse?.visitType?.toUpperCase() === "OP" ? ( //
            <></>
          ) : (
            <Col xs={24} sm={12} md={6} lg={6} xl={6}>
              <Form.Item
                label="Remarks"
                name="remarks"
                style={{ color: "#575757" }}
                rules={[
                  {
                    required: true,
                    message: "Remarks is required!",
                  },
                ]}
              >
                <Input
                  placeholder="Please input Remarks"
                  style={{ color: "black" }}
                  disabled={portalSubmission}
                />
              </Form.Item>
            </Col>
          )}

          {loading ? (
            <div
              style={{
                margin: "auto",
                width: "100%,",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Spin />
            </div>
          ) : (
            documentRequestResponseDocuments?.["requestedDocuments"]?.length >
              0 && (
              <div style={{ width: "100%" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "98%",
                  }}
                >
                  <div
                    style={{
                      textDecoration: "underline",
                      fontSize: "16px",
                      margin: "16px 8px",
                      fontWeight: "bold",
                    }}
                  >
                    Request Document Upload
                  </div>

                  <div style={{ display: "flex", gap: 8 }}>
                    {/* {patients?.referralInfoResponse?.cghsReferralResponse
                      ?.id ? (
                      <Button
                        type="primary"
                        style={{
                          backgroundColor: "rgb(0, 76, 143)",
                          color: "#fff",
                          boxShadow: "0px 2px 8px 0px #00000040",
                        }}
                        onClick={() => setReferralChecklistModal(true)}
                      >
                        Investigation Report Checklist
                      </Button>
                    ) : null} */}
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 10,
                  }}
                >
                  {documentRequestResponseDocuments?.["requestedDocuments"]
                    ?.length > 0 &&
                    documentRequestResponseDocuments?.[
                      "requestedDocuments"
                    ]?.map((docType: any) => {
                      const matchingDocuments = documentUrls?.filter(
                        (doc) => doc.documentLabelType?.toUpperCase() === docType.label?.toUpperCase()
                      );

                      if (matchingDocuments?.length > 0 && !docType?.multiple) {
                        return null;
                      }

                      return (
                        <div
                          key={docType.id}
                          style={{
                            border: "1px solid #eee",
                            padding: 10,
                            borderRadius: 8,
                            display: "flex",
                            alignItems: "center",
                            // gap: 8,
                          }}
                        >
                          {matchingDocuments?.length > 0 &&
                          !docType?.multiple ? (
                            <p></p>
                          ) : (
                            <Upload
                              multiple={docType?.multiple}
                              {...(docType?.multiple
                                ? { fileList: uploadFileList }
                                : {})}
                              showUploadList={false}
                              beforeUpload={() => false}
                              onChange={
                                handleFileChange &&
                                handleFileChange(
                                  docType?.labelId,
                                  docType?.multiple
                                )
                              }
                              disabled={portalSubmission}
                            >
                              <Button
                                icon={<UploadOutlined />}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  borderRadius: 0,
                                  border: "none",
                                  borderTopLeftRadius: "6px",
                                  borderBottomLeftRadius: "6px",
                                }}
                                // disabled={
                                //   docType.label === "HOSPITAL BILL" ||
                                //   docType.label === "BILL SUMMARY"
                                //     ? true
                                //     : (auth?.role?.role === "FO_STAFF" &&
                                //         claimData?.intimated) ||
                                //       portalSubmission ||
                                //       isApproved
                                // }
                                disabled={
                                  // docType.label === "HOSPITAL BILL" ||
                                  docType.label === "BILL SUMMARY" || docType?.label?.toUpperCase() === "DISCHARGE PHOTO" || docType?.label?.toUpperCase() === "AUDIT REPORT"
                                    ? true
                                    : portalSubmission
                                }
                              ></Button>
                            </Upload>
                          )}
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <Button
                              style={{
                                borderRadius: 0,
                                border: "none",
                                paddingLeft: 4,
                                borderTopRightRadius: "6px",
                                borderBottomRightRadius: "6px",
                              }}
                              iconPosition={"end"}
                              // icon={
                              //   matchingDocuments?.length > 0 ? (
                              //     <EyeOutlined
                              //       style={{ marginLeft: "0.6rem" }}
                              //       onClick={() => {
                              //         handleOpen(
                              //           matchingDocuments?.[0]
                              //             ?.documentLabelType
                              //         );
                              //       }}
                              //     />
                              //   ) : (
                              //     <></>
                              //   )
                              // }
                            >
                              {docType?.label}{" "}
                              {docType?.required ||
                              (docType?.label?.toUpperCase() ===
                                "SUPPORTING DOCUMENT" &&
                                isSupportingDocument === "true") ? (
                                <span style={{ color: "red" }}>*</span>
                              ) : (
                                <></>
                              )}
                            </Button>
                          </div>

                          {/* {matchingDocuments?.length > 0 && (
                <EyeOutlined
                  style={{ marginLeft: "0.6rem" }}
                  onClick={() => {
                    handleOpen();
                  }}
                />
              )} */}
                        </div>
                      );
                    })}
                </div>

              </div>
            )
          )}

          {/* <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 10,
              borderTop:
                updatedRequestDocumentUrls?.length > 0
                  ? "1px solid gray"
                  : "none",
            }}
          >
            {updatedRequestDocumentUrls?.map((docType: any, index: any) => (
              <div
                key={docType.id}
                style={{
                  border: "1px solid #eee",
                  padding: 10,
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",

                  // gap: 8,
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Button
                    style={{
                      borderRadius: "6px",
                      border: "none",
                      paddingLeft: 20,
                      // borderTopRightRadius: "6px",
                      // borderBottomRightRadius: "6px",
                      background: docType?.deficiencyCheckStatus
                        ? "rgb(0, 109, 119)"
                        : docType?.deficiencyCheckStatus === "false" ||
                          docType?.deficiencyCheckStatus === false
                        ? "#DD2025"
                        : "#fff",
                      color:
                        docType?.deficiencyCheckStatus ||
                        docType?.deficiencyCheckStatus === false
                          ? "white"
                          : "black",
                    }}
                    iconPosition={"end"}
                    icon={
                      <EyeOutlined
                        style={{ marginLeft: "0.6rem" }}
                        onClick={() => {
                          handleOpen(docType?.documentLabelType);
                        }}
                      />
                    }
                  >
                    {docType?.documentLabelType} {index+1}{" "}
                  </Button>
                </div>
              </div>
            ))} 
            
          </div> */}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 10,
              borderTop:
                updatedRequestDocumentUrls?.length > 0
                  ? "1px solid gray"
                  : "none",
              width: "100%",
            }}
          >
            {Object.entries(
              groupByLabelType(updatedRequestDocumentUrls || [])
            )?.map(([labelType, docs]: [any, any]) =>
              docs.map((docType: any, idx: any) => (
                <div
                  key={docType.id}
                  style={{
                    border: "1px solid #eee",
                    padding: 10,
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",

                    // gap: 8,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Button
                      style={{
                        borderRadius: "6px",
                        border: "none",
                        paddingLeft: 20,
                        // borderTopRightRadius: "6px",
                        // borderBottomRightRadius: "6px",
                        background: docType?.deficiencyCheckStatus
                          ? "rgb(0, 109, 119)"
                          : docType?.deficiencyCheckStatus === "false" ||
                            docType?.deficiencyCheckStatus === false
                          ? "#DD2025"
                          : "#fff",
                        color:
                          docType?.deficiencyCheckStatus ||
                          docType?.deficiencyCheckStatus === false
                            ? "white"
                            : "black",
                      }}
                      iconPosition={"end"}
                      icon={
                        <EyeOutlined
                          style={{ marginLeft: "0.6rem" }}
                          onClick={() => {
                            handleOpen(
                              docType?.documentLabelType,
                              docType?.documentUrl
                            );
                          }}
                        />
                      }
                    >
                      {
                        docType?.documentLabelType?.length > 30 ? (
                          <Tooltip title={docType?.documentLabelType}>
                            {docType?.documentLabelType?.substring(0, 30) + "..."}
                          </Tooltip>
                        ) : (
                          docType?.documentLabelType
                        )
                      } {idx + 1}{" "}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>

          {billRequiredDocuments?.length > 0 ? (
            <div style={{ width: "100%", marginTop: 16 }}>
              <SupportingDocumentChecklistModal
                inlineMode
                documents={billRequiredDocuments}
                claimExtraDocuments={claimExtraDocuments}
                portalSubmission={portalSubmission}
                onCancel={() => null}
                onFileSelect={(doc, checkedItems, file, allUploads) =>
                  onSupportingFileSelect?.(doc, checkedItems, file, allUploads)
                }
                onView={(record, documentUrl) =>
                  onViewSupportingDocument?.(record, documentUrl)
                }
                onDownload={onDownloadSupportingDocument}
                onDelete={(record, documentUrl) =>
                  onDeleteSupportingDocument?.(record, documentUrl)
                }
              />
            </div>
          ) : null}

          {documentRequestResponseDocuments?.["responseDocuments"]?.length >
            0 && (
            <div style={{ width: "100%" }}>
              <div
                style={{
                  textDecoration: "underline",
                  fontSize: "16px",
                  margin: "16px 8px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                Response Document Upload
                {showData && portalSubmission ? (
                  <Tooltip title={"Submit Response Documents"}>
                    <CheckOutlined onClick={handleSaveData} />{" "}
                  </Tooltip>
                ) : (
                  <></>
                )}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 10,
                  opacity: portalSubmission ? 1 : 0.5,
                  pointerEvents: portalSubmission ? "auto" : "none",
                }}
              >
                {documentRequestResponseDocuments?.["responseDocuments"]
                  ?.length > 0 &&
                  documentRequestResponseDocuments?.["responseDocuments"]?.map(
                    (docType: any) => {
                      const matchingDocuments = documentUrls?.filter(
                        (doc) => doc.documentLabelType === docType.label
                      );

                      if (matchingDocuments?.length > 0 && !docType?.multiple) {
                        return null;
                      }

                      return (
                        <div
                          key={docType.id}
                          style={{
                            border: "1px solid #eee",
                            padding: 10,
                            borderRadius: 8,
                            display: "flex",
                            alignItems: "center",
                            // gap: 8,
                          }}
                        >
                          <Upload
                            multiple={docType?.multiple}
                            {...(docType?.multiple
                              ? { fileList: uploadFileList }
                              : {})}
                            showUploadList={false}
                            beforeUpload={() => false}
                            onChange={
                              handleFileChange &&
                              handleFileChange(
                                docType?.labelId,
                                docType?.multiple
                              )
                            }
                            disabled={!portalSubmission}
                          >
                            <Button
                              icon={<UploadOutlined />}
                              style={{
                                // width: "100%",
                                display: "flex",
                                alignItems: "center",
                                borderRadius: 0,
                                border: "none",
                                borderTopLeftRadius: "6px",
                                borderBottomLeftRadius: "6px",
                              }}
                              // disabled={!showData}
                              disabled={!portalSubmission}
                            >
                              {/* {docType.label}{" "}
                  {docType?.required ? (
                    <span style={{ color: "red" }}>*</span>
                  ) : (
                    <></>
                  )} */}
                            </Button>
                          </Upload>
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <Button
                              style={{
                                borderRadius: 0,
                                border: "none",
                                paddingLeft: 0,
                                borderTopRightRadius: "6px",
                                borderBottomRightRadius: "6px",
                              }}
                              iconPosition={"end"}
                              // icon={
                              //   matchingDocuments?.length > 0 ? (
                              //     <EyeOutlined
                              //       style={{ marginLeft: "0.6rem" }}
                              //       onClick={() => {
                              //         handleOpen(
                              //           matchingDocuments?.[0]
                              //             ?.documentLabelType
                              //         );
                              //       }}
                              //     />
                              //   ) : (
                              //     <></>
                              //   )
                              // }
                            >
                              {docType.label}{" "}
                              {docType?.required ? (
                                <span style={{ color: "red" }}>*</span>
                              ) : (
                                <></>
                              )}
                            </Button>
                          </div>

                          {/* {matchingDocuments?.length > 0 && (
                <EyeOutlined
                  style={{ marginLeft: "0.6rem" }}
                  onClick={() => {
                    handleOpen();
                  }}
                />
              )} */}
                        </div>
                      );
                    }
                  )}
              </div>
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 10,
              borderTop:
                updatedResponseDocumentUrls?.length > 0
                  ? "1px solid gray"
                  : "none",
              width: "100%",
            }}
          >
            {/* {updatedResponseDocumentUrls?.map((docType: any, index: any) => (
              <div
                key={docType.id}
                style={{
                  border: "1px solid #eee",
                  padding: 10,
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",

                  // gap: 8,
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Button
                    style={{
                      borderRadius: "6px",
                      border: "none",
                      paddingLeft: 20,
                      // borderTopRightRadius: "6px",
                      // borderBottomRightRadius: "6px",
                      background: docType?.deficiencyCheckStatus
                        ? "rgb(0, 109, 119)"
                        : docType?.deficiencyCheckStatus === "false" ||
                          docType?.deficiencyCheckStatus === false
                        ? "#DD2025"
                        : "#fff",
                      color:
                        docType?.deficiencyCheckStatus ||
                        docType?.deficiencyCheckStatus === false
                          ? "white"
                          : "black",
                    }}
                    iconPosition={"end"}
                    icon={
                      <EyeOutlined
                        style={{ marginLeft: "0.6rem" }}
                        onClick={() => {
                          handleOpen(docType?.documentLabelType);
                        }}
                      />
                    }
                  >
                    {docType?.documentLabelType} {index + 1}{" "}
                  </Button>
                </div>
              </div>
            ))} */}

            {Object.entries(
              groupByLabelType(updatedResponseDocumentUrls || [])
            )?.map(([labelType, docs]: [any, any]) =>
              docs.map((docType: any, idx: any) => (
                <div
                  key={docType.id}
                  style={{
                    border: "1px solid #eee",
                    padding: 10,
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",

                    // gap: 8,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Button
                      style={{
                        borderRadius: "6px",
                        border: "none",
                        paddingLeft: 20,
                        // borderTopRightRadius: "6px",
                        // borderBottomRightRadius: "6px",
                        background: docType?.deficiencyCheckStatus
                          ? "rgb(0, 109, 119)"
                          : docType?.deficiencyCheckStatus === "false" ||
                            docType?.deficiencyCheckStatus === false
                          ? "#DD2025"
                          : "#fff",
                        color:
                          docType?.deficiencyCheckStatus ||
                          docType?.deficiencyCheckStatus === false
                            ? "white"
                            : "black",
                      }}
                      iconPosition={"end"}
                      icon={
                        <EyeOutlined
                          style={{ marginLeft: "0.6rem" }}
                          onClick={() => {
                            handleOpen(
                              docType?.documentLabelType,
                              docType?.documentUrl
                            );
                          }}
                        />
                      }
                    >
                      {docType?.documentLabelType} {idx + 1}{" "}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>

          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <Form.Item name="checkbox1" style={{ color: "#575757" }}>
              <Checkbox checked={true}>
                I, the undersigned, hereby declare that the claim submitted for
                the above patient under the Central Government Health Scheme
                (CGHS) has not been raised under any other scheme or insurance
                policy. This claim is exclusive to CGHS, and no duplicate or
                overlapping claims have been submitted elsewhere by this
                hospital. Nothing material has been concealed regarding the
                claim submitted. If I am found to be guilty of any misconduct or
                unlawful activity, then liability as per the laws of the land,
                rules, and regulations shall be imposed accordingly.
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <div className="buttons" style={{ margin: "16px 0" }}>
          {
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.8rem",
              }}
            >
              {claimData?.submitted || claimData?.initiated ? (
                // || claimData?.approved
                <></>
              ) : !caseClaimId ? (
                hasFunctionality("claimCreation") && (
                  <div style={{ width: "16%" }}>
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: "rgb(0, 76, 143)",
                        color: "#fff",
                        boxShadow: "0px 2px 8px 0px #00000040",
                        width: "100%",
                      }}
                      // onClick={handleSubmit}
                      onClick={() => handleSaveConfirmationModal("save")}
                      loading={isSaving}
                    >
                      SAVE
                    </Button>
                  </div>
                )
              ) : (
                hasFunctionality("claimCreation") && (
                  // !(auth?.role?.role == "FO_STAFF" && claimData?.intimated) &&
                  <div style={{ width: "16%" }}>
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: "rgb(0, 76, 143)",
                        color: "#fff",
                        boxShadow: "0px 2px 8px 0px #00000040",
                        width: "100%",
                      }}
                      // onClick={handleSubmit}
                      onClick={() => handleSaveConfirmationModal("update")}
                      loading={isSaving}
                    >
                      Update
                    </Button>
                  </div>
                )
              )}

              {hasFunctionality("claimIntimation") ? (
                currentActiveStage?.visitStage == "Claim" &&
                (claimData?.intimated ||
                  claimData?.submitted ||
                  claimData?.initiated ||
                  claimData?.approved) ? (
                  <></>
                ) : (
                  <div style={{ width: "16%" }}>
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: "rgb(0, 76, 143)",
                        color: "#fff",
                        boxShadow: "0px 2px 8px 0px #00000040",
                        width: "100%",
                      }}
                      // onClick={handleIntimate}
                      onClick={() => handleSaveConfirmationModal("intimate")}
                      loading={isIntimating}
                    >
                      Send To QC
                    </Button>
                  </div>
                )
              ) : (
                <></>
              )}

              {/* {patients?.visitInfoResponse?.visitType?.toUpperCase() === "OP" &&
                (hasFunctionality("opdBillList") ||
                  hasFunctionality("ipdBillList")) && (
                  <div style={{ width: "16%" }}>
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: "rgb(0, 76, 143)",
                        color: "#fff",
                        boxShadow: "0px 2px 8px 0px #00000040",
                        width: "100%",
                        opacity: !isApproved ? 1 : 0.5,
                        pointerEvents: !isApproved ? "auto" : "none",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (patients?.visitInfoResponse?.id) {
                          navigate({
                            pathname: `${ROUTES.PATIENT_BILLING_LIST}/${patients?.casesResponse?.id}/${patients?.visitInfoResponse?.id}`,
                          });
                        }
                      }}
                    >
                      Bill Audit
                    </Button>
                  </div>
                )} */}
              {/*{(patients?.visitInfoResponse?.visitType?.toUpperCase() ===
                "IP" ||
                patients?.visitInfoResponse?.visitType?.toUpperCase() ===
                  "EMERGENCY") &&
                hasFunctionality("ipdBillList") && (
                  <div style={{ width: "16%" }}>
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: "rgb(0, 76, 143)",
                        color: "#fff",
                        boxShadow: "0px 2px 8px 0px #00000040",
                        width: "100%",
                        opacity: !isApproved ? 1 : 0.5,
                        pointerEvents: !isApproved ? "auto" : "none",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (patients?.visitInfoResponse?.id) {
                          navigate({
                            pathname: `${ROUTES.PATIENT_BILLING_LIST}/${patients?.casesResponse?.id}/${patients?.visitInfoResponse?.id}`,
                          });
                        }
                      }}
                    >
                      Bill Audit
                    </Button>
                  </div>
                )} */}

              {hasFunctionality("claimApprover") &&
                !claimData?.initiated &&
                !claimData?.approved &&
                !claimData?.submitted && (
                  <div style={{ width: "16%" }}>
                    <Button
                      loading={isApprovedInitiated}
                      type="primary"
                      style={{
                        backgroundColor: "rgb(0, 76, 143)",
                        color: "#fff",
                        boxShadow: "0px 2px 8px 0px #00000040",
                        width: "100%",
                        opacity: !isApproved ? 1 : 0.5,
                        pointerEvents: !isApproved ? "auto" : "none",
                      }}
                      // onClick={handleApprove}
                      onClick={() => handleSaveConfirmationModal("approve")}
                      disabled={isApproved}
                    >
                      Qc Approve
                    </Button>
                  </div>
                )}

              {claimData?.submitted || claimData?.initiated ? (
                <></>
              ) : (
                hasFunctionality("claimSubmission") && (
                  <div style={{ width: "16%" }}>
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: "rgb(0, 76, 143)",
                        color: "#fff",
                        boxShadow: "0px 2px 8px 0px #00000040",
                        width: "100%",
                      }}
                      // onClick={handleSubmitOnPortal}
                      onClick={() => handleSaveConfirmationModal("submit")}
                      loading={isSubmitting}
                    >
                      {patients?.casesResponse?.integratedWithPortal
                        ? "Initiate"
                        : "Submit Manually"}
                    </Button>
                  </div>
                )
              )}
            </div>
          }
        </div>
      </Form>

      <ReferralChecklistModal
        visible={referralChecklistModal}
        handleCancel={() => setReferralChecklistModal(false)}
        referralId={patients?.referralInfoResponse?.cghsReferralResponse?.id}
        documentId={
          patients?.referralInfoResponse?.cghsReferralResponse?.documentId
        }
      />
    </div>
  );
};

export default ClaimCghsNew;
